import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IProductStoreListItem } from '../../shared/models/inventory/product-store-list-item';
import { IProductStore } from '../../shared/models/inventory/product-store';
import { IPagedResult } from '../../shared/models/paged-result';
import { IStoreMovementFilterSet } from '../../shared/models/inventory/store-movement-filter-set';
import { serialize } from '../../utility/serialize';
import { IStoreMovementListItem } from '../../shared/models/inventory/store-movement-list-item';
import { IStoreMovementItem } from '../../shared/models/inventory/store-movement-item';
import { IStoreMovement } from '../../shared/models/inventory/store-movement';
import { IStoreMovementDespatch } from '../../shared/models/inventory/store-movement-despatch';
import { IReceiveStoreMovement } from '../../shared/models/inventory/receive-store-movement';
import { IStoreMovementTypeListItem } from '../../shared/models/inventory/store-movement-type-list-item';
import { BaseFilterSet } from '../../shared/models/base-filter-set';
import { IProductCatalogFilterSet } from '../../shared/models/product/product-catalog-filter-set';
import { IProductCatalogListItem } from '../../shared/models/product/product-catalog-list-item';
import { IProductCatalog } from '../../shared/models/product/product-catalog';

const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  baseQuery,
  tagTypes: ['ProductStores', 'StoreMovements', 'StoreMovement'],
  endpoints: (builder) => ({
    getProductsByWarehouseId: builder.query<IProductStoreListItem[], string>({
      query: (warehouseId) => {
        return {
          url: `/api/productStores/warehouse/${warehouseId}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, warehouseId) => [{ type: 'ProductStores', id: warehouseId }],
    }),
    createProductStore: builder.mutation<IProductStoreListItem, IProductStore>({
      query: (body) => ({
        url: '/api/productStores',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProductStores'],
    }),
    updateProductStore: builder.mutation<IProductStoreListItem, IProductStore>({
      query: (body) => ({
        url: '/api/productStores',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ProductStores'],
    }),
    deleteProductStore: builder.mutation<boolean, string>({
      query: (id) => ({
        url: `/api/productStores/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProductStores'],
    }),
    getStoreMovements: builder.query<
      IPagedResult<IStoreMovementListItem>,
      IStoreMovementFilterSet | string | void
    >({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '{"page":"1","size":"50"}';
        return {
          url: `/api/storeMovements${qs}`,
          method: 'GET',
        };
      },
      providesTags: (result) => [
        { type: 'StoreMovements', id: 'LIST' },
        ...(result?.results?.map((item) => ({ type: 'StoreMovement' as const, id: item.id })) ??
          []),
      ],
    }),
    createStoreMovement: builder.mutation<IProductStoreListItem, IStoreMovement>({
      query: (body) => ({
        url: '/api/storeMovements',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'StoreMovements', id: 'LIST' }],
    }),
    getStoreMovementById: builder.query<IStoreMovementItem, string>({
      query: (movementId) => {
        return {
          url: `/api/storeMovements/${movementId}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, movementId) => [{ type: 'StoreMovement', id: movementId }],
    }),
    storeMovementDespatchAdvice: builder.mutation<IProductStoreListItem, IStoreMovementDespatch>({
      query: (body) => ({
        url: '/api/storeMovements/despatchAdvice',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'StoreMovements', id: 'LIST' }],
    }),
    receiveStoreMovement: builder.mutation<IProductStoreListItem, IReceiveStoreMovement>({
      query: (body) => ({
        url: '/api/storeMovements/receive',
        method: 'POST',
        body,
      }),
    }),
    cancelStoreMovement: builder.mutation<boolean, string>({
      query: (movementId) => ({
        url: `/api/storeMovements/${movementId}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, movementId) => [
        { type: 'StoreMovement', id: movementId },
        { type: 'StoreMovements', id: 'LIST' },
      ],
    }),
    getStoreMovementTypes: builder.query<IPagedResult<IStoreMovementTypeListItem>, BaseFilterSet>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '{"size":"-1"}';
        return {
          url: `/api/storeMovements/types${qs}`,
          method: 'GET',
        };
      },
    }),
    releaseProductSkuStore: builder.mutation<boolean, string>({
      query: (productSkuStoreId) => ({
        url: `/api/productStores/release/${productSkuStoreId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['ProductStores'],
    }),
    getProductCatalogsForMovement: builder.query<
      IPagedResult<IProductCatalogListItem>,
      IProductCatalogFilterSet | string | void
    >({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '{"page":"1","size":"50"}';
        return {
          url: `/api/storeMovements/product-catalog${qs}`,
          method: 'GET',
        };
      },
    }),
    getProductWithSkusForMovement: builder.query<IProductCatalog[], IProductCatalogFilterSet>({
      query: (filterSet) => {
        const qs = filterSet ? `${serialize(filterSet)}` : '';
        return {
          url: `/api/storeMovements/product-catalog/list?${qs}`,
          method: 'GET',
        };
      },
    }),
  }),
});

export { inventoryApi };

export const {
  useLazyGetProductsByWarehouseIdQuery,
  useGetProductsByWarehouseIdQuery,
  useCreateProductStoreMutation,
  useUpdateProductStoreMutation,
  useDeleteProductStoreMutation,
  useLazyGetStoreMovementsQuery,
  useLazyGetStoreMovementByIdQuery,
  useCreateStoreMovementMutation,
  useStoreMovementDespatchAdviceMutation,
  useReceiveStoreMovementMutation,
  useCancelStoreMovementMutation,
  useLazyGetStoreMovementTypesQuery,
  useReleaseProductSkuStoreMutation,
  useLazyGetProductCatalogsForMovementQuery,
  useLazyGetProductWithSkusForMovementQuery,
} = inventoryApi;
